/**
 * Log — utility di logging centralizzato per Diakonix.
 *
 * Due usi pensati:
 *
 * 1. Codice con DI (componenti, servizi, guard, interceptor):
 *      private logger = inject(LoggerService);
 *      this.logger.debug('foo', payload);
 *
 *    Vedi `logger.service.ts` per il wrapper Angular.
 *
 * 2. Codice senza DI (modelli, helpers, utility statici):
 *      import { Log } from 'src/app/services/shared/log';
 *      Log.debug('foo', payload);
 *
 *    Stessa implementazione, stessa configurazione globale di livello.
 *
 * Tutti i `console.*` esistenti vengono inoltre filtrati globalmente
 * via `installConsoleFilter()`, chiamata da `main.ts` al bootstrap.
 * Quindi anche un vecchio `console.log` rispetta il livello configurato
 * — finché non viene riscritto a `Log.debug` organicamente.
 */

export enum LogLevel {
  Debug = 0,
  Info = 1,
  Warn = 2,
  Error = 3,
  Silent = 4,
}

let currentLevel: LogLevel = LogLevel.Debug;

/**
 * Imposta il livello di log a runtime. Chiamato una volta in `main.ts`
 * con il valore di `environment.LOG_LEVEL`.
 */
export function setLogLevel(level: LogLevel | string): void {
  if (typeof level === 'string') {
    const normalized = level.toLowerCase();
    const map: Record<string, LogLevel> = {
      debug: LogLevel.Debug,
      info: LogLevel.Info,
      warn: LogLevel.Warn,
      error: LogLevel.Error,
      silent: LogLevel.Silent,
    };
    currentLevel = map[normalized] ?? LogLevel.Debug;
  } else {
    currentLevel = level;
  }
}

export function getLogLevel(): LogLevel {
  return currentLevel;
}

// Riferimenti originali al console nativo, presi PRIMA dell'eventuale
// installazione del filter (così il filter chiama queste, non sé stesso).
const nativeConsole = {
  log: console.log.bind(console),
  info: console.info ? console.info.bind(console) : console.log.bind(console),
  warn: console.warn.bind(console),
  error: console.error.bind(console),
  debug: console.debug ? console.debug.bind(console) : console.log.bind(console),
};

export const Log = {
  debug: (...args: unknown[]): void => {
    if (currentLevel <= LogLevel.Debug) nativeConsole.log(...args);
  },
  info: (...args: unknown[]): void => {
    if (currentLevel <= LogLevel.Info) nativeConsole.info(...args);
  },
  warn: (...args: unknown[]): void => {
    if (currentLevel <= LogLevel.Warn) nativeConsole.warn(...args);
  },
  error: (...args: unknown[]): void => {
    if (currentLevel <= LogLevel.Error) nativeConsole.error(...args);
  },
};

/**
 * Installa un filter globale su `window.console.*` che rispetta il
 * livello configurato. Effetto: i ~1300 `console.log` esistenti nel
 * codebase smettono di girare in prod (livello warn/error) e in dev a
 * livelli alti, senza dover essere riscritti uno per uno.
 *
 * Chiamare UNA SOLA volta, in `main.ts`, dopo `setLogLevel(...)`.
 *
 * Idempotente: se la funzione viene chiamata due volte (es. hot reload
 * in dev), reinstalla i wrapper ripartendo dai riferimenti `native*`
 * salvati alla prima chiamata.
 */
let consoleFilterInstalled = false;
export function installConsoleFilter(): void {
  if (consoleFilterInstalled) return;
  consoleFilterInstalled = true;

  console.log = (...args: unknown[]) => {
    if (currentLevel <= LogLevel.Debug) nativeConsole.log(...args);
  };
  console.debug = (...args: unknown[]) => {
    if (currentLevel <= LogLevel.Debug) nativeConsole.debug(...args);
  };
  console.info = (...args: unknown[]) => {
    if (currentLevel <= LogLevel.Info) nativeConsole.info(...args);
  };
  console.warn = (...args: unknown[]) => {
    if (currentLevel <= LogLevel.Warn) nativeConsole.warn(...args);
  };
  console.error = (...args: unknown[]) => {
    if (currentLevel <= LogLevel.Error) nativeConsole.error(...args);
  };
}
